import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import {
  ArrowForwardIcon,
  BookmarkOutlineIcon,
  CancelledIcon,
  ChatBubbleInstaIcon,
  ChatBubbleOutlineIcon,
  FacebookShareIcon,
  HeartIcon,
  MoreHorizIcon,
  PublicIcon,
  SendIcon,
  ThumbUpIcon,
} from 'images'
import CarouselMedia from './CarouselMedia'

// Meta single-image feed ad card — post header (page icon + name + "Sponsored"
// + overflow menu), body text with "see more", media, link bar, and action row.
// Covers Facebook Feed Desktop/Mobile and Instagram Feed (Figma `31504:26175`,
// `31504:26226`, `31504:26274`). Instagram Reels is structurally different —
// see ReelsAdCard.
//
// Adapted from v1's Previews/Facebook.jsx and Previews/Instagram.jsx — same
// CSS classes, remapped to the current ad variation shape.
//
// When `ad.carouselCards` is present, the media area swaps to CarouselMedia's
// multi-card strip instead of the single media + link bar block.
const FeedAdCard = ({ platform, ad, mobileView = false }) => {
  const bodyRef = useRef(null)
  const [isLongText, setIsLongText] = useState(false)
  const [showMore, setShowMore] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    if (bodyRef.current) setIsLongText(bodyRef.current.scrollHeight > bodyRef.current.clientHeight)
  }, [])

  const toggleShowMore = () => setShowMore((prev) => !prev)

  const {
    pageName,
    profilePictureUrl,
    body,
    headline,
    callToAction,
    destinationUrl,
    media,
    carouselCards,
  } = ad
  const isCarousel = Array.isArray(carouselCards) && carouselCards.length > 0
  const isVideo = Boolean(media?.type?.includes('video'))
  const displayUrl = destinationUrl?.replace(/^https?:\/\//, '').replace('www.', '')

  const avatar = (iconClass, pictureClass) =>
    profilePictureUrl ? (
      <img alt={pageName} className={pictureClass} src={profilePictureUrl} />
    ) : (
      <div className={iconClass} />
    )

  if (platform === 'instagram') {
    return (
      <div className="instagram px-0 py-3">
        <div className="d-flex justify-content-between gap-2 px-3">
          <div className="d-flex align-items-center flex-row w-100">
            <div>{avatar('instagram__icon', 'instagram__profile_picture')}</div>
            {pageName ? (
              <p className="instagram__title">
                <strong>{pageName}</strong>
                <span className="subtitle">Sponsored</span>
              </p>
            ) : (
              <div className="fb-error">{t('Recommendations.summary.fbPageInfoError')}</div>
            )}
          </div>
          <div>
            <MoreHorizIcon width={30} />
          </div>
        </div>
        <div>
          {isCarousel ? (
            <CarouselMedia platform="instagram" cards={carouselCards} callToAction={callToAction} />
          ) : (
            <>
              <div className="instagram__link">
                {isVideo ? (
                  <video
                    className="instagram__link_media"
                    src={media.url}
                    autoPlay
                    loop
                    muted
                    playsInline
                  />
                ) : (
                  // visibleByDefault skips LazyLoadImage's IntersectionObserver gate —
                  // see the fuller rationale comment in CarouselMedia.jsx.
                  <LazyLoadImage
                    className="instagram__link_media"
                    src={media?.url}
                    alt={media?.filename}
                    width={media?.width}
                    height={media?.height}
                    visibleByDefault
                  />
                )}
                <div className="instagram__link_order-now">
                  <span>{callToAction}</span>
                  <span>
                    <ArrowForwardIcon />
                  </span>
                </div>
              </div>
              <div className="divider" />
            </>
          )}
          <div className="instagram__interactions">
            <div className="d-flex">
              <span>
                <HeartIcon />
              </span>
              <span>
                <ChatBubbleInstaIcon />
              </span>
              <span>
                <SendIcon />
              </span>
            </div>
            <BookmarkOutlineIcon height={25} />
          </div>
        </div>
        <p className="instagram__caption">
          <span ref={bodyRef} className={showMore ? 'text' : 'text_clipped'}>
            {body}
          </span>
          {isLongText && (
            <span className="more" onClick={toggleShowMore}>
              {showMore ? 'less' : 'more'}
            </span>
          )}
        </p>
      </div>
    )
  }

  const isTinyMedia = media?.width < 320 && media?.height < 200
  const mainStyle = mobileView ? 'mobile' : ''

  return (
    <div className={`facebook ${mainStyle} p-0 pt-3`}>
      <div className="d-flex justify-content-between px-3">
        <div className="d-flex align-items-center flex-row pr-2 w-100">
          <div>{avatar('icon', 'profile_picture')}</div>
          {pageName ? (
            <p className="title">
              <strong>{pageName}</strong>
              <span className="subtitle">
                <span className="pr-1">Sponsored</span>
                <span>
                  <PublicIcon width={15} />
                </span>
              </span>
            </p>
          ) : (
            <div className="fb-error">{t('Recommendations.summary.fbPageInfoError')}</div>
          )}
        </div>
        <div className="actions d-flex align-items-baseline gap-2">
          <span>
            <MoreHorizIcon width={mobileView ? 24 : 30} />
          </span>
          {mobileView && (
            <span>
              <CancelledIcon width={18} />
            </span>
          )}
        </div>
      </div>

      <p className="px-3">
        <span ref={bodyRef} className={showMore ? 'text' : 'text_clipped'}>
          {body}
        </span>
        {mobileView && isLongText && (
          <span className="more" onClick={toggleShowMore}>
            {showMore ? 'See less' : 'See more'}
          </span>
        )}
      </p>

      <div>
        {isCarousel ? (
          <CarouselMedia
            platform="facebook"
            cards={carouselCards}
            mobileView={mobileView}
            callToAction={callToAction}
          />
        ) : (
          <div className={`link ${isTinyMedia ? 'tiny-media' : ''}`}>
            {isVideo ? (
              <video className="media" src={media.url} autoPlay loop muted playsInline />
            ) : (
              // visibleByDefault skips LazyLoadImage's IntersectionObserver gate —
              // see the fuller rationale comment in CarouselMedia.jsx.
              <LazyLoadImage
                className="media"
                alt={media?.filename}
                src={media?.url}
                height={media?.height}
                width={media?.width}
                visibleByDefault
              />
            )}
            <div className="order-now">
              <div className="text d-flex flex-column gap-1">
                <div className="url" title={destinationUrl}>
                  {mobileView ? displayUrl : displayUrl?.toUpperCase()}
                </div>
                <div className="headline">{headline}</div>
              </div>
              <div className="button">{callToAction}</div>
            </div>
          </div>
        )}
        {mobileView && <div className="divider" />}

        <div className="interactions">
          <button>
            <ThumbUpIcon />
            <span>Like</span>
          </button>
          <button>
            <ChatBubbleOutlineIcon />
            <span>Comment</span>
          </button>
          <button>
            <FacebookShareIcon />
            <span>Share</span>
          </button>
        </div>
      </div>
    </div>
  )
}

FeedAdCard.propTypes = {
  platform: PropTypes.oneOf(['facebook', 'instagram']).isRequired,
  mobileView: PropTypes.bool,
  ad: PropTypes.shape({
    pageName: PropTypes.string,
    profilePictureUrl: PropTypes.string,
    body: PropTypes.string,
    headline: PropTypes.string,
    callToAction: PropTypes.string,
    destinationUrl: PropTypes.string,
    media: PropTypes.object,
    carouselCards: PropTypes.array,
  }).isRequired,
}

export default FeedAdCard
